import { useState } from 'react';
import PageTitle from '../components/common/PageTitle';
import { Truck, Shield, Hammer, Wrench, ArrowRight, ChevronRight } from 'lucide-react';

// Types for equipment data
interface EquipmentItem {
  id: string;
  name: string;
  description: string;
  image: string;
  details: {
    label: string;
    value: string;
  }[];
}

interface EquipmentCategory {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  items: EquipmentItem[];
}

const OpremPage = () => {
  // State to track which equipment item is selected
  const [selectedCategory, setSelectedCategory] = useState<string>('vehicles');
  const [selectedItem, setSelectedItem] = useState<string | null>(null);

  // Equipment data
  const equipmentCategories: EquipmentCategory[] = [
    {
      id: 'vehicles',
      name: 'Vozila',
      icon: <Truck className="h-6 w-6" />,
      description: 'Naša gasilska vozila, ki jih uporabljamo za intervencije in prevoz opreme.',
      items: [
        {
          id: 'gvc-16-25',
          name: 'GVC 16/25',
          description: 'Gasilsko vozilo s cisterno za gašenje požarov in prevoz moštva.',
          image: '/images/vehicles/gvc-16-25.jpg',
          details: [
            { label: 'Znamka', value: 'MAN TGM 13.290' },
            { label: 'Letnik', value: '2018' },
            { label: 'Prostornina rezervoarja', value: '2500 l' },
            { label: 'Črpalka', value: 'NH 30' },
            { label: 'Posadka', value: '1+6' },
            { label: 'Namen', value: 'Gašenje požarov in tehnično reševanje' }
          ]
        },
        {
          id: 'gvgp-1',
          name: 'GVGP-1',
          description: 'Manjše gasilsko vozilo za gašenje gozdnih požarov z visokotlačno črpalko.',
          image: '/images/vehicles/gvgp-1.jpg',
          details: [
            { label: 'Znamka', value: 'Toyota Hilux' },
            { label: 'Letnik', value: '2020' },
            { label: 'Prostornina rezervoarja', value: '300 l' },
            { label: 'Črpalka', value: 'Rosenbauer UHPS' },
            { label: 'Posadka', value: '1+4' },
            { label: 'Namen', value: 'Gašenje gozdnih požarov' }
          ]
        },
        {
          id: 'gvm-1',
          name: 'GVM-1',
          description: 'Gasilsko vozilo za prevoz moštva na intervencije in dogodke.',
          image: '/images/vehicles/gvm-1.jpg',
          details: [
            { label: 'Znamka', value: 'Volkswagen Transporter T6' },
            { label: 'Letnik', value: '2019' },
            { label: 'Posadka', value: '1+8' },
            { label: 'Namen', value: 'Prevoz moštva in manjše opreme' }
          ]
        }
      ]
    },
    {
      id: 'protective-equipment',
      name: 'Zaščitna oprema',
      icon: <Shield className="h-6 w-6" />,
      description: 'Osebna zaščitna oprema, ki jo uporabljajo naši gasilci pri intervencijah.',
      items: [
        {
          id: 'fire-suit',
          name: 'Zaščitna obleka',
          description: 'Zaščitna obleka za gašenje požarov v objektih.',
          image: '/images/protective/fire-suit.jpg',
          details: [
            { label: 'Proizvajalec', value: 'Rosenbauer' },
            { label: 'Model', value: 'Fire Max 3' },
            { label: 'Standard', value: 'EN 469' },
            { label: 'Toplotna zaščita', value: 'Visoka' },
            { label: 'Vidljivost', value: 'Odsevni trakovi' }
          ]
        },
        {
          id: 'helmet',
          name: 'Zaščitna čelada',
          description: 'Čelada za zaščito glave pri gašenju požarov.',
          image: '/images/protective/helmet.jpg',
          details: [
            { label: 'Proizvajalec', value: 'MSA' },
            { label: 'Model', value: 'Gallet F1 XF' },
            { label: 'Standard', value: 'EN 443' },
            { label: 'Dodatki', value: 'Integrirana svetilka, ščitnik za obraz' }
          ]
        },
        {
          id: 'scba',
          name: 'Izolirni dihalni aparat',
          description: 'Dihalni aparat za zaščito dihal v zadimljenih prostorih.',
          image: '/images/protective/scba.jpg',
          details: [
            { label: 'Proizvajalec', value: 'Dräger' },
            { label: 'Model', value: 'PSS 3000' },
            { label: 'Tlak', value: '300 bar' },
            { label: 'Avtonomija', value: '30-45 min' },
            { label: 'Teža', value: '10 kg' }
          ]
        }
      ]
    },
    {
      id: 'technical-equipment',
      name: 'Tehnična oprema',
      icon: <Hammer className="h-6 w-6" />,
      description: 'Oprema za tehnično reševanje in druge intervencije.',
      items: [
        {
          id: 'hydraulic-tools',
          name: 'Hidravlično orodje',
          description: 'Orodje za reševanje ukleščenih oseb iz vozil.',
          image: '/images/technical/hydraulic-tools.jpg',
          details: [
            { label: 'Proizvajalec', value: 'Holmatro' },
            { label: 'Tip', value: 'Baterijski pogon' },
            { label: 'Vsebuje', value: 'Škarje, razpiralo, cilinder' },
            { label: 'Sila razpiranja', value: 'Do 280 kN' },
            { label: 'Sila rezanja', value: 'Do 1100 kN' }
          ]
        },
        {
          id: 'chainsaw',
          name: 'Motorna žaga',
          description: 'Motorna žaga za reševanje ob naravnih nesrečah.',
          image: '/images/technical/chainsaw.jpg',
          details: [
            { label: 'Proizvajalec', value: 'Stihl' },
            { label: 'Model', value: 'MS 462 R' },
            { label: 'Moč', value: '4.4 kW' },
            { label: 'Dolžina meča', value: '50 cm' },
            { label: 'Namen', value: 'Odstranjevanje padlih dreves' }
          ]
        },
        {
          id: 'generator',
          name: 'Električni generator',
          description: 'Generator za napajanje opreme pri intervencijah.',
          image: '/images/technical/generator.jpg',
          details: [
            { label: 'Proizvajalec', value: 'Honda' },
            { label: 'Model', value: 'EU 22i' },
            { label: 'Moč', value: '2.2 kW' },
            { label: 'Avtonomija', value: '8-10 ur' },
            { label: 'Gorivo', value: 'Bencin' }
          ]
        }
      ]
    },
    {
      id: 'pump-equipment',
      name: 'Črpalke in cevi',
      icon: <Wrench className="h-6 w-6" />,
      description: 'Oprema za črpanje vode in gašenje požarov.',
      items: [
        {
          id: 'portable-pump',
          name: 'Prenosna motorna črpalka',
          description: 'Črpalka za črpanje vode iz odprtih virov.',
          image: '/images/pumps/portable-pump.jpg',
          details: [
            { label: 'Proizvajalec', value: 'Rosenbauer' },
            { label: 'Model', value: 'FOX 4' },
            { label: 'Pretok', value: 'Do 1600 l/min' },
            { label: 'Tlak', value: 'Do 10 bar' },
            { label: 'Motor', value: 'Briggs & Stratton, 18 HP' }
          ]
        },
        {
          id: 'submersible-pump',
          name: 'Potopna črpalka',
          description: 'Črpalka za črpanje vode iz poplavljenih prostorov.',
          image: '/images/pumps/submersible-pump.jpg',
          details: [
            { label: 'Proizvajalec', value: 'Rosenbauer' },
            { label: 'Model', value: 'NAUTILUS 8/1' },
            { label: 'Pretok', value: 'Do 800 l/min' },
            { label: 'Tlak', value: 'Do 1 bar' },
            { label: 'Napajanje', value: 'Električno, 230V' }
          ]
        },
        {
          id: 'hoses',
          name: 'Gasilske cevi',
          description: 'Cevi za dovod vode do mesta požara.',
          image: '/images/pumps/hoses.jpg',
          details: [
            { label: 'Tipi', value: 'A, B, C, D' },
            { label: 'Premeri', value: 'Od 25mm do 110mm' },
            { label: 'Dolžine', value: '15m, 20m' },
            { label: 'Delovni tlak', value: 'Do 16 bar' },
            { label: 'Material', value: 'Sintetična vlakna z notranjo gumo' }
          ]
        }
      ]
    }
  ];

  // Get currently selected category
  const currentCategory = equipmentCategories.find(cat => cat.id === selectedCategory) || equipmentCategories[0];
  
  // Get currently selected item
  const currentItem = selectedItem 
    ? currentCategory.items.find(item => item.id === selectedItem) 
    : null;

  return (
    <>
      <PageTitle 
        title="Oprema" 
        subtitle="Spoznajte našo sodobno gasilsko opremo"
        backgroundImage="/images/equipment-hero.jpg"
      />

      <section className="page-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Sidebar - Categories */}
            <div className="md:col-span-1">
              <h2 className="text-xl font-bold text-secondary mb-4">Kategorije opreme</h2>
              <nav className="space-y-2">
                {equipmentCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => {
                      setSelectedCategory(category.id);
                      setSelectedItem(null);
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg flex items-center transition-colors ${
                      selectedCategory === category.id
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
                    }`}
                  >
                    <span className={`mr-3 ${selectedCategory === category.id ? 'text-white' : 'text-primary'}`}>
                      {category.icon}
                    </span>
                    <span>{category.name}</span>
                  </button>
                ))}
              </nav>

              <div className="mt-8 bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-secondary mb-2">Potrebujete pomoč?</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Za več informacij o naši opremi nas kontaktirajte.
                </p>
                <a href="/kontakt" className="text-primary hover:text-primary-700 text-sm font-medium flex items-center">
                  Kontaktirajte nas
                  <ArrowRight className="h-4 w-4 ml-1" />
                </a>
              </div>
            </div>

            {/* Main Content */}
            <div className="md:col-span-3">
              {!currentItem ? (
                // Category overview
                <>
                  <h2 className="text-2xl font-bold text-secondary mb-2">{currentCategory.name}</h2>
                  <p className="text-gray-600 mb-6">{currentCategory.description}</p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {currentCategory.items.map((item) => (
                      <div 
                        key={item.id}
                        className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow cursor-pointer"
                        onClick={() => setSelectedItem(item.id)}
                      >
                        <div className="h-48 overflow-hidden">
                          <img 
                            src={item.image} 
                            alt={item.name} 
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" 
                          />
                        </div>
                        <div className="p-4">
                          <h3 className="text-xl font-semibold mb-2">{item.name}</h3>
                          <p className="text-gray-600 text-sm mb-4 line-clamp-2">{item.description}</p>
                          <button className="text-primary hover:text-primary-700 font-medium flex items-center text-sm">
                            Več podrobnosti
                            <ChevronRight className="h-4 w-4 ml-1" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                // Item detail view
                <div>
                  <button 
                    onClick={() => setSelectedItem(null)}
                    className="text-primary hover:text-primary-700 font-medium flex items-center mb-6"
                  >
                    <ArrowRight className="h-4 w-4 mr-1 rotate-180" />
                    Nazaj na {currentCategory.name}
                  </button>
                  
                  <div className="bg-white rounded-xl overflow-hidden shadow-md p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      <div>
                        <div className="rounded-lg overflow-hidden mb-6">
                          <img 
                            src={currentItem.image} 
                            alt={currentItem.name} 
                            className="w-full h-auto" 
                          />
                        </div>
                      </div>
                      
                      <div>
                        <h2 className="text-2xl font-bold text-secondary mb-2">{currentItem.name}</h2>
                        <p className="text-gray-700 mb-6">{currentItem.description}</p>
                        
                        <h3 className="text-lg font-semibold text-secondary mb-4">Tehnične specifikacije</h3>
                        <div className="space-y-2">
                          {currentItem.details.map((detail, index) => (
                            <div key={index} className="py-2 border-b border-gray-100 flex">
                              <div className="w-1/3 font-medium text-gray-700">{detail.label}</div>
                              <div className="w-2/3 text-gray-900">{detail.value}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-gray-50 py-12">
        <div className="container-custom text-center">
          <h2 className="text-2xl font-bold text-secondary mb-4">Želite izvedeti več?</h2>
          <p className="text-gray-700 mb-8 max-w-2xl mx-auto">
            Za več informacij o naši opremi ali če vas zanima, kako lahko pomagate društvu pri nakupu nove opreme, nas kontaktirajte.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="/kontakt" className="btn-primary">
              Kontaktirajte nas
            </a>
            <a href="/donacija" className="btn-outline">
              Donacija dohodnine
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default OpremPage;